// SyncTeX parser for bidirectional source ↔ PDF navigation
// Parses .synctex or .synctex.gz files generated by pdflatex/xelatex/lualatex -synctex=1

import pako from 'pako';

export interface SyncTexEntry {
  page: number;
  x: number;      // PDF x coordinate in points (72 DPI, from left)
  y: number;      // PDF y coordinate in points (72 DPI, from bottom)
  width: number;
  height: number;
  file: string;
  line: number;
}

export interface SyncTexData {
  entries: SyncTexEntry[];
  inputFiles: Map<number, string>;
}

export interface ForwardSyncResult {
  page: number;
  y: number;
  height: number;
}

export interface InverseSyncResult {
  file: string;
  line: number;
}

/**
 * Parse SyncTeX data from base64-encoded .synctex.gz content
 */
export function parseSyncTex(base64Data: string): SyncTexData {
  // Decode base64 to binary
  const binaryString = atob(base64Data);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  // Decompress gzip
  let text: string;
  try {
    const decompressed = pako.inflate(bytes);
    text = new TextDecoder().decode(decompressed);
  } catch {
    // Maybe it's not gzipped, try as plain text
    text = new TextDecoder().decode(bytes);
  }

  return parseSyncTexText(text);
}

/**
 * Parse raw SyncTeX text content
 *
 * SyncTeX format overview:
 *   SyncTeX Version:1
 *   Input:1:./main.tex
 *   Input:2:./chapter1.tex
 *   ...
 *   Content:
 *   {1           <- page 1 begins
 *   [1,42:0,...  <- vbox: tag,line:x,y,w,h,d
 *   (1,1:72,...  <- hbox
 *   x1,10:72,... <- current/kern node
 *   ]            <- end vbox
 *   }            <- page 1 ends
 */
function parseSyncTexText(text: string): SyncTexData {
  const inputFiles = new Map<number, string>();
  const entries: SyncTexEntry[] = [];

  const lines = text.split('\n');
  let currentPage = 0;
  let inContent = false;

  for (const line of lines) {
    // Parse Input declarations
    const inputMatch = line.match(/^Input:(\d+):(.+)$/);
    if (inputMatch) {
      const tag = parseInt(inputMatch[1], 10);
      let filePath = inputMatch[2].trim();
      // Normalize path: remove leading ./
      if (filePath.startsWith('./')) {
        filePath = filePath.substring(2);
      }
      inputFiles.set(tag, filePath);
      continue;
    }

    // Detect content section
    if (line.trim() === 'Content:') {
      inContent = true;
      continue;
    }

    if (!inContent) continue;

    // Page begin: {pageNum
    const pageBegin = line.match(/^\{(\d+)$/);
    if (pageBegin) {
      currentPage = parseInt(pageBegin[1], 10);
      continue;
    }

    // Page end
    if (line.trim() === '}') {
      continue;
    }

    // Parse box records: [tag,line:x,y:w,h,d  (vbox)
    //                     (tag,line:x,y:w,h,d  (hbox)
    // Parse node records: xtag,line:x,y       (current)
    //                     ktag,line:x,y       (kern)
    //                     gtag,line:x,y       (glue)
    //                     $tag,line:x,y       (math)

    // Match box records: [tag,line:x,y:w,h,d or (tag,line:x,y:w,h,d
    const boxMatch = line.match(/^[\[\(](\d+),(\d+):(-?\d+),(-?\d+):(-?\d+),(-?\d+),(-?\d+)/);
    if (boxMatch) {
      const tag = parseInt(boxMatch[1], 10);
      const lineNum = parseInt(boxMatch[2], 10);
      const x = parseInt(boxMatch[3], 10);
      const y = parseInt(boxMatch[4], 10);
      const w = parseInt(boxMatch[5], 10);
      const h = parseInt(boxMatch[6], 10);
      const d = parseInt(boxMatch[7], 10);

      const fileName = inputFiles.get(tag) || '';
      if (fileName && lineNum > 0) {
        entries.push({
          page: currentPage,
          x: x / 65536,       // SyncTeX uses scaled points (sp), convert to bp
          y: y / 65536,
          width: w / 65536,
          height: (h + d) / 65536,
          file: fileName,
          line: lineNum,
        });
      }
      continue;
    }

    // Match node records: x|k|g|$tag,line:x,y
    const nodeMatch = line.match(/^[xkg\$](\d+),(\d+):(-?\d+),(-?\d+)/);
    if (nodeMatch) {
      const tag = parseInt(nodeMatch[1], 10);
      const lineNum = parseInt(nodeMatch[2], 10);
      const x = parseInt(nodeMatch[3], 10);
      const y = parseInt(nodeMatch[4], 10);

      const fileName = inputFiles.get(tag) || '';
      if (fileName && lineNum > 0) {
        entries.push({
          page: currentPage,
          x: x / 65536,
          y: y / 65536,
          width: 0,
          height: 12, // default height for nodes (roughly one line)
          file: fileName,
          line: lineNum,
        });
      }
      continue;
    }
  }

  return { entries, inputFiles };
}

/**
 * Forward sync: source file + line → PDF page + position
 * Returns the best match for the given source location.
 */
export function forwardSync(
  data: SyncTexData,
  file: string,
  line: number
): ForwardSyncResult | null {
  // Normalize file path
  let normalizedFile = file;
  if (normalizedFile.startsWith('./')) {
    normalizedFile = normalizedFile.substring(2);
  }

  // Find all entries for this file and line
  let bestMatch: SyncTexEntry | null = null;
  let bestDist = Infinity;

  for (const entry of data.entries) {
    if (entry.file !== normalizedFile) continue;

    const dist = Math.abs(entry.line - line);
    if (dist < bestDist) {
      bestDist = dist;
      bestMatch = entry;
    }
    // Exact match — prefer first occurrence
    if (dist === 0) break;
  }

  if (!bestMatch) return null;

  return {
    page: bestMatch.page,
    y: bestMatch.y,
    height: bestMatch.height || 12,
  };
}

/**
 * Inverse sync: PDF page + coordinates → source file + line
 * Returns the closest source location to the click position.
 */
export function inverseSync(
  data: SyncTexData,
  page: number,
  x: number,
  y: number
): InverseSyncResult | null {
  // Filter entries for this page
  const pageEntries = data.entries.filter(e => e.page === page);
  if (pageEntries.length === 0) return null;

  // Find closest entry by Y distance (primary), then X (secondary)
  let bestMatch: SyncTexEntry | null = null;
  let bestDist = Infinity;

  for (const entry of pageEntries) {
    const dy = Math.abs(entry.y - y);
    const dx = Math.abs(entry.x - x);
    const dist = dy * 10 + dx; // Weight Y more heavily since lines are horizontal

    if (dist < bestDist) {
      bestDist = dist;
      bestMatch = entry;
    }
  }

  if (!bestMatch) return null;

  return {
    file: bestMatch.file,
    line: bestMatch.line,
  };
}
